import { Helmet } from 'react-helmet-async';

// PER-PAGE SEO.
//
// Why this exists: the site is a single-page app served through a catch-all
// redirect to one index.html, so EVERY route was shipping the same <title> and the
// same meta description — about twenty landing pages that search engines could not
// tell apart. Anything rendering <Seo> gets its own title, description, canonical
// and social card.
//
// Two rules worth keeping:
//   1. One <Seo> per route, near the top of the page component.
//   2. `canonical` is always the absolute production URL for that route. Netlify
//      serves the same app on deploy-preview and branch subdomains, and without a
//      canonical those get indexed as duplicates of the real page.

export const SITE_URL = 'https://alfredvoice.com';
// NO DEFAULT og:image, deliberately.
//
// The first version pointed at /images/alfred-og.png, a filename I assumed rather
// than checked — it has never existed, and because the SPA catch-all answers every
// unmatched path with index.html it returned 200 with text/html, so scrapers got an
// HTML document where a PNG should be and previews came out blank while the tag
// looked correct.
//
// The obvious substitutes are worse than nothing. Every shipped image in
// public/images is from the RECEPTIONIST era — alfred-desk.png has a monitor in it
// reading "Reception & Support / HR & Interviews / Employee Helpdesk / Answers every
// call 24/7", which is the positioning this site dropped and which the chat backend
// now explicitly contradicts. A share card advertising the product we stopped
// selling is worse than a card with no image.
//
// So: omit the tags unless a caller passes a real one. When a transportation image
// exists (1200×630 is the target), set it here and every page picks it up.
const DEFAULT_OG_IMAGE = null;

export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  // Pass `false` for pages that must never be indexed (thank-you pages, anything
  // behind auth, anything duplicated at another URL).
  index = true,
  // Any number of JSON-LD objects. Rendered as separate script tags because
  // Google parses each independently and one malformed blob shouldn't void them all.
  structuredData = []
}) {
  const canonical = `${SITE_URL}${path}`;
  const blocks = Array.isArray(structuredData) ? structuredData : [structuredData];

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {!index && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Alfred Voice" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {image && <meta property="og:image" content={image} />}

      {/* summary_large_image with no image renders an empty card — fall back to the
          text-only summary card when we have nothing worth showing. */}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}

      {blocks.filter(Boolean).map((data, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

// ─── Structured-data helpers ────────────────────────────────────────────────
// Kept here so the shapes stay consistent across pages. Schema.org types are the
// ones Google actually surfaces: FAQPage drives the expandable Q&A result, and
// Organization feeds the knowledge panel.

export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (items || []).map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer }
    }))
  };
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Alfred Voice',
    url: SITE_URL,
    logo: `${SITE_URL}/alfred-voice-favicon.svg`,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-346-258-0006',
        contactType: 'sales',
        areaServed: 'US',
        availableLanguage: ['English']
      }
    ]
  };
}

export function breadcrumbSchema(trail) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: (trail || []).map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.path}`
    }))
  };
}
